import React from "react";
import { motion } from "framer-motion";
import { CheckCircle2, ArrowUpRight } from "lucide-react";
import { Link } from "react-router-dom";
import { BRAND } from "../lib/brand";

const PRINCIPLES = [
  "Disciplined process over simple networking",
  "Stakeholder alignment before execution",
  "Project controls from day one",
  "Capital structured around outcomes",
];

export const AboutSection = () => {
  return (
    <section
      id="about"
      data-testid="about-section"
      className="relative bg-white py-24 lg:py-32"
    >
      <div className="mx-auto max-w-7xl px-6 lg:px-8">
        <div className="grid grid-cols-1 gap-14 lg:grid-cols-12 lg:gap-20">
          {/* Left: copy */}
          <div className="lg:col-span-7">
            <div className="mb-4 inline-flex items-center gap-2 rounded-full border border-slate-200 bg-slate-50 px-3 py-1.5">
              <span className="h-1.5 w-1.5 rounded-full bg-[#00A3E0]" />
              <span className="text-[11px] font-semibold uppercase tracking-[0.22em] text-slate-600">
                About Us
              </span>
            </div>
            <h2 className="font-display text-4xl font-extrabold leading-[1.05] tracking-tight text-slate-900 sm:text-5xl">
              An umbrella platform for{" "}
              <span className="text-[#00A3E0]">high-value consortiums</span>.
            </h2>
            <p className="mt-6 max-w-2xl text-lg leading-relaxed text-slate-600">
              <span className="font-semibold text-slate-900">
                Comprehensive Professional Solutions Consulting (CPS Consulting)
              </span>{" "}
              is a business development, project management, and strategic
              advisory firm. We convene, structure, and advance complex
              large-scale developments — connecting stakeholders, capital, and
              technical expertise across energy, real estate, infrastructure,
              media, and commodities.
            </p>
            <p className="mt-4 max-w-2xl text-base leading-relaxed text-slate-600">
              Where most firms stop at advice, CPS positions itself as a{" "}

              <span className="font-semibold text-slate-900">consortium builder</span>{" "}
              — bridging the gap between vision and execution for multi-stakeholder
              projects, from conceptual planning to implementation readiness.
            </p>

            <div className="mt-10 grid grid-cols-1 gap-3 sm:grid-cols-2">
              {PRINCIPLES.map((p, i) => (
                <motion.div
                  key={p}
                  initial={{ opacity: 0, y: 12 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: "-80px" }}
                  transition={{ duration: 0.5, delay: i * 0.08 }}
                  className="flex items-start gap-3 rounded-xl border border-slate-200 bg-slate-50/60 p-4"
                >
                  <CheckCircle2 className="mt-0.5 h-5 w-5 flex-none text-[#00A3E0]" />
                  <span className="text-sm font-medium text-slate-700">{p}</span>
                </motion.div>
              ))}
            </div>

            <div className="mt-10 flex items-center gap-4">
              <Link
                to="/submit-project"
                data-testid="about-submit-cta"
                className="group inline-flex items-center gap-2 rounded-full bg-[#0F172A] px-6 py-3 text-sm font-semibold text-white hover:bg-[#1e293b]"
              >
                Bring us your project
                <ArrowUpRight className="h-4 w-4 transition-transform group-hover:rotate-45" />
              </Link>
              <a
                href="#services"
                className="text-sm font-semibold text-[#00A3E0] underline-offset-4 hover:underline"
              >
                See how we work →
              </a>
            </div>
          </div>

          {/* Right: image + founder card */}
          <div className="relative lg:col-span-5">
            <motion.div
              initial={{ opacity: 0, scale: 0.96 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true, margin: "-80px" }}
              transition={{ duration: 0.7 }}
              className="relative overflow-hidden rounded-2xl border border-slate-200 bg-slate-100"
            >
              <img
                src="https://images.unsplash.com/photo-1543269865-cbf427effbad?auto=format&fit=crop&w=1200&q=80"
                alt="CPS Consulting diverse leadership team strategy session"
                className="aspect-[4/5] w-full object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-[#0F172A]/70 via-transparent to-transparent" />
              <div className="absolute bottom-6 left-6 right-6 rounded-xl border border-white/20 bg-white/10 p-4 backdrop-blur-xl">
                <p className="text-[10px] font-semibold uppercase tracking-[0.32em] text-white/70">
                  Founder & CEO
                </p>
                <p className="mt-1 font-display text-xl font-bold text-white">
                  {BRAND.founder}
                </p>
                <p className="mt-1 text-xs text-white/75">
                  Architect of CPS Consulting&apos;s consortium-building methodology.
                </p>
              </div>
            </motion.div>

            {/* Floating accent card */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="absolute -left-4 -top-4 hidden rounded-2xl border border-[#00A3E0]/20 bg-white p-5 shadow-2xl shadow-[#00A3E0]/10 sm:block"
            >
              <p className="text-[10px] font-semibold uppercase tracking-[0.28em] text-[#00A3E0]">
                Methodology
              </p>
              <p className="mt-1 font-display text-lg font-bold leading-tight text-slate-900">
                Concept → <br />Implementation
              </p>
            </motion.div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default AboutSection;
